/**
 * Rasterizing PDFs in the browser keeps artwork handling in one place: the
 * rest of the app (public invitation, emails, link previews) only ever deals
 * with images, and the API needs no native PDF tooling in Lambda.
 *
 * pdf.js is loaded on demand — it is a megabyte of parser nobody needs until
 * someone actually picks a PDF, and it touches browser-only globals at import
 * time.
 */

/** Render scale — 2x keeps print-resolution artwork crisp on retina screens. */
const DEFAULT_SCALE = 2;

/** Widest edge we rasterize to, so a poster-size PDF cannot blow up memory. */
const MAX_DIMENSION = 2400;

/**
 * Most pages we import from one PDF. Every rendered page is held in memory
 * until the upload finishes, and an invitation this long is a wrong file
 * rather than a real invitation — so refuse it instead of freezing the tab.
 */
const MAX_PAGES = 20;

export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

/** `invitation.pdf` → `invitation-p1.png` */
function pageFileName(sourceName: string, pageNumber: number): string {
  const base = sourceName.replace(/\.pdf$/i, '') || 'invitation';
  return `${base}-p${pageNumber}.png`;
}

async function loadPdfJs() {
  const [pdfjs, worker] = await Promise.all([
    import('pdfjs-dist'),
    // Vite ships the worker as its own asset; pdf.js refuses to parse without it.
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ]);
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  return pdfjs;
}

async function canvasToPngFile(canvas: HTMLCanvasElement, fileName: string): Promise<File> {
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Could not render the PDF page to an image');
  return new File([blob], fileName, { type: 'image/png' });
}

/**
 * Convert every page of a PDF to a PNG `File`, in page order. A multi-page PDF
 * therefore becomes a multi-page invitation with no extra work from the user.
 */
export async function pdfToImageFiles(file: File, scale = DEFAULT_SCALE): Promise<File[]> {
  const pdfjs = await loadPdfJs();
  const data = await file.arrayBuffer();
  const loadingTask = pdfjs.getDocument({ data });
  const doc = await loadingTask.promise;
  try {
    if (doc.numPages > MAX_PAGES) {
      throw new Error(
        `That PDF has ${doc.numPages} pages — invitations are limited to ${MAX_PAGES}.`,
      );
    }
    const files: File[] = [];
    for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber += 1) {
      const page = await doc.getPage(pageNumber);
      const base = page.getViewport({ scale: 1 });
      // Poster-size artwork is scaled down rather than rendered at the full
      // requested multiple — a 4x render of a 33" page would exhaust memory.
      // longEdge is at least 1, so the cap is always a positive scale — it is
      // never floored back up above MAX_DIMENSION.
      const longEdge = Math.max(base.width, base.height, 1);
      const viewport = page.getViewport({ scale: Math.min(scale, MAX_DIMENSION / longEdge) });
      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Could not render the PDF page to an image');
      await page.render({ canvas, canvasContext: context, viewport }).promise;
      files.push(await canvasToPngFile(canvas, pageFileName(file.name, pageNumber)));
      // Drop the backing bitmap now that the PNG is out — otherwise a
      // multi-page import holds every canvas alive alongside its blob.
      canvas.width = 0;
      canvas.height = 0;
    }
    return files;
  } finally {
    // Releases the worker and the document's buffers — a multi-page artwork
    // upload would otherwise hold every rendered page in memory.
    await loadingTask.destroy();
  }
}

/** Long edge of a file tile's picture, in pixels — small, since it is a tile. */
const THUMBNAIL_EDGE = 320;

/**
 * A picture of a PDF's first page, as an object URL for a file tile — or null
 * when it cannot be drawn (not a PDF, unreadable, a password, a network
 * failure). The tile then keeps its icon; a thumbnail is never worth an error.
 *
 * pdf.js asks for byte ranges, so a long PDF is not downloaded whole to draw
 * its cover. The caller owns the URL and may revoke it.
 */
export async function pdfFirstPageThumbnail(url: string): Promise<string | null> {
  let loadingTask: { promise: Promise<unknown>; destroy(): Promise<void> } | undefined;
  try {
    const pdfjs = await loadPdfJs();
    const task = pdfjs.getDocument({ url });
    loadingTask = task;
    const doc = await task.promise;
    const page = await doc.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({
      scale: THUMBNAIL_EDGE / Math.max(base.width, base.height, 1),
    });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));
    const context = canvas.getContext('2d');
    if (!context) return null;
    await page.render({ canvas, canvasContext: context, viewport }).promise;
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    canvas.width = 0;
    canvas.height = 0;
    return blob ? URL.createObjectURL(blob) : null;
  } catch {
    return null;
  } finally {
    await loadingTask?.destroy().catch(() => undefined);
  }
}
